import {doQuerySafe, doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  GetFormulaWeightQuery as GetFormulaWeightQueryResponse,
  GetFormulaWeightQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetFormulaWeightQuery';
import {GET_FORMULA_WEIGHT_QUERY} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {
  CustomResolutionQueryType,
  GetFormulaWeightQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {CustomEvalResult} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
import {UNIT_TYPES_QUERY} from 'units/UnitsConstants';

function preloadUnitTypes(): void {
  doQuerySafe<UnitTypesQuery>({query: UNIT_TYPES_QUERY}, {}).catch(() => {});
}

export async function bulkResolveGetFormulaWeightToServerResolutionResult(
  queries: Array<GetFormulaWeightQuery>
): Promise<Array<ServerResolutionResult>> {
  preloadUnitTypes();

  const moleculeIds = queries.map((query) => query.moleculeId);

  const queryResult = await doQueryWithoutCache<
    GetFormulaWeightQueryResponse,
    GetFormulaWeightQueryVariables
  >({query: GET_FORMULA_WEIGHT_QUERY}, {variables: {moleculeIds}});

  const formulaWeightByMoleculeId = new Map<string, number | null>();
  for (const molecule of queryResult.molecules) {
    if (isInaccessible(molecule)) {
      formulaWeightByMoleculeId.set(molecule.invalidId, null);
    } else {
      const formulaWeight = molecule.currentVersion.chemicalStructure.formulaWeight;
      if (formulaWeight.__typename === 'FloatPropertyNode') {
        formulaWeightByMoleculeId.set(molecule.id, formulaWeight.value);
      } else {
        formulaWeightByMoleculeId.set(molecule.id, null);
      }
    }
  }

  return queries.map((query) => {
    const formulaWeight = formulaWeightByMoleculeId.get(query.moleculeId);
    if (formulaWeight == null) {
      return makeCustomResolutionResultFromTypedValue(null, 'Could not retrieve formula weight');
    }
    return makeCustomResolutionResultFromTypedValue({
      type: FieldType.FLOAT,
      value: String(formulaWeight),
      rawValue: String(formulaWeight),
      floatValue: formulaWeight,
    });
  });
}

export function getFormulaWeightColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  const bioentityTypedValue = cellDependencies[0];

  if (bioentityTypedValue == null || bioentityTypedValue.type !== BaseTypedValueType.BIOENTITY) {
    return {val: typedValue, error: null, locked: false};
  }

  const moleculeId = bioentityTypedValue.bioentitySummary.id;

  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_FORMULA_WEIGHT,
      moleculeId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
